import React, { useState } from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const VerifyRequired: React.FC = () => {
  const { user, logout } = useAuth();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleResend = async () => {
    setStatus('sending');
    try {
      await api.post('/api/auth/resend-verification');
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: '#f5f7fb',
        px: 2,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 3,
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 50px rgba(7,24,45,0.08)',
          borderColor: 'rgba(7,24,45,0.08)',
        }}
      >
        <Stack spacing={2.5} alignItems="center">
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: 'rgba(255,201,40,0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MarkEmailUnreadIcon sx={{ fontSize: 36, color: '#b98500' }} />
          </Box>

          <Box>
            <Typography variant="h5" fontWeight={800} mb={0.75}>
              Verify your email
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We sent a verification link to{' '}
              <strong>{user?.email}</strong>. Click the link in that email to
              activate your account and access all features.
            </Typography>
          </Box>

          {status === 'sent' && (
            <Typography variant="body2" color="success.main" fontWeight={600}>
              Verification email resent. Check your inbox.
            </Typography>
          )}
          {status === 'error' && (
            <Typography variant="body2" color="error">
              Failed to resend. Please try again.
            </Typography>
          )}

          <Stack spacing={1.5} width="100%">
            <Button
              variant="contained"
              fullWidth
              onClick={handleResend}
              disabled={status === 'sending' || status === 'sent'}
              sx={{ bgcolor: '#ffc928', color: '#07182d', fontWeight: 800, '&:hover': { bgcolor: '#f3b51b' } }}
              startIcon={status === 'sending' ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {status === 'sending' ? 'Sending…' : status === 'sent' ? 'Email sent ✓' : 'Resend verification email'}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => { logout(); }}
              sx={{ borderColor: 'rgba(7,24,45,0.15)', color: 'text.secondary' }}
            >
              Sign out
            </Button>
          </Stack>

          <Typography variant="caption" color="text.disabled">
            Already verified? Refresh the page or sign in again.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default VerifyRequired;
